/**
 * Alta y edición de una tarea. El mismo formulario para las dos cosas: no había
 * forma de editar una tarea —había que borrarla y volver a crearla—, y mantener
 * dos formularios distintos es la manera segura de que acaben divergiendo.
 */
import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import type { Task, TaskPriority, TaskRecurrencia, TaskTipo } from '@/lib/types';
import SelectorRelacion, { type Relacion } from './SelectorRelacion';
import {
  ETIQUETA_PRIORIDAD, ETIQUETA_RECURRENCIA, ETIQUETA_TIPO,
  aInputLocal, atajoFecha, deInputLocal,
} from './tipos';

export interface DatosTarea {
  title: string;
  description: string | null;
  tipo: TaskTipo;
  priority: TaskPriority;
  dueDate: string | null;
  remindAt: string | null;
  recurrencia: TaskRecurrencia;
  responsable: string | null;
  relatedType: Relacion['relatedType'];
  relatedId: string | null;
  relatedName: string | null;
}

interface Props {
  abierto: boolean;
  /** La tarea a editar, o `null` para crear una nueva. */
  tarea: Task | null;
  /** Relación fijada de antemano: al crear desde la ficha de un lead. */
  relacionFija?: Relacion;
  /** Nombres ya usados en otras tareas, para sugerir sin inventar una lista. */
  responsables?: string[];
  onGuardar: (datos: DatosTarea) => Promise<void>;
  onEliminar?: () => Promise<void>;
  onCerrar: () => void;
}

function inicial(tarea: Task | null, relacionFija?: Relacion): DatosTarea {
  if (tarea) {
    return {
      title: tarea.title,
      description: tarea.description,
      tipo: tarea.tipo,
      priority: tarea.priority,
      dueDate: tarea.dueDate,
      remindAt: tarea.remindAt,
      recurrencia: tarea.recurrencia,
      responsable: tarea.responsable,
      relatedType: tarea.relatedType,
      relatedId: tarea.relatedId,
      relatedName: tarea.relatedName,
    };
  }
  return {
    title: '',
    description: null,
    tipo: 'otro',
    priority: 'medium',
    dueDate: null,
    remindAt: null,
    recurrencia: 'none',
    responsable: null,
    relatedType: relacionFija?.relatedType ?? null,
    relatedId: relacionFija?.relatedId ?? null,
    relatedName: relacionFija?.relatedName ?? null,
  };
}

export default function TareaModal({
  abierto, tarea, relacionFija, responsables = [], onGuardar, onEliminar, onCerrar,
}: Props) {
  // `key` en el <Formulario> reinicia el estado al cambiar de tarea: sin eso,
  // abrir una tarea después de otra enseñaba los datos de la anterior.
  return (
    <Modal isOpen={abierto} onClose={onCerrar} title={tarea ? 'Editar tarea' : 'Nueva tarea'} size="lg">
      {abierto && (
        <Formulario
          key={tarea?.id || 'nueva'}
          datosIniciales={inicial(tarea, relacionFija)}
          relacionBloqueada={!!relacionFija}
          responsables={responsables}
          onGuardar={onGuardar}
          onEliminar={onEliminar}
          onCerrar={onCerrar}
        />
      )}
    </Modal>
  );
}

function Formulario({
  datosIniciales, relacionBloqueada, responsables, onGuardar, onEliminar, onCerrar,
}: {
  datosIniciales: DatosTarea;
  relacionBloqueada: boolean;
  responsables: string[];
  onGuardar: (datos: DatosTarea) => Promise<void>;
  onEliminar?: () => Promise<void>;
  onCerrar: () => void;
}) {
  const [datos, setDatos] = useState<DatosTarea>(datosIniciales);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);

  const set = <K extends keyof DatosTarea>(campo: K, valor: DatosTarea[K]) =>
    setDatos(d => ({ ...d, [campo]: valor }));

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!datos.title.trim()) { setError('El título es obligatorio'); return; }

    setGuardando(true);
    setError(null);
    try {
      await onGuardar({ ...datos, title: datos.title.trim() });
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar la tarea');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={enviar} className="space-y-4">
      <div>
        <label className="label">Título *</label>
        <input
          className="input"
          autoFocus
          value={datos.title}
          onChange={e => set('title', e.target.value)}
          placeholder="Llamar a…"
        />
      </div>

      <div>
        <label className="label">Descripción</label>
        <textarea
          className="input"
          rows={2}
          value={datos.description || ''}
          onChange={e => set('description', e.target.value || null)}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="label">Tipo</label>
          <select className="input" value={datos.tipo} onChange={e => set('tipo', e.target.value as TaskTipo)}>
            {(Object.keys(ETIQUETA_TIPO) as TaskTipo[]).map(t => (
              <option key={t} value={t}>{ETIQUETA_TIPO[t]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Prioridad</label>
          <select className="input" value={datos.priority} onChange={e => set('priority', e.target.value as TaskPriority)}>
            {(Object.keys(ETIQUETA_PRIORIDAD) as TaskPriority[]).map(p => (
              <option key={p} value={p}>{ETIQUETA_PRIORIDAD[p]}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="label">Responsable</label>
          {/* Texto libre con sugerencias en vez de una lista cerrada: el CRM no
              tiene un padrón de usuarios, y mantener uno a mano en Configuración
              solo para esto sería una pantalla más que cuidar. */}
          <input
            className="input"
            list="responsables-tareas"
            value={datos.responsable || ''}
            onChange={e => set('responsable', e.target.value || null)}
            placeholder="Sin asignar"
          />
          <datalist id="responsables-tareas">
            {responsables.map(r => <option key={r} value={r} />)}
          </datalist>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label">Vence</label>
          <input
            type="datetime-local"
            className="input"
            value={aInputLocal(datos.dueDate)}
            onChange={e => set('dueDate', deInputLocal(e.target.value))}
          />
          <div className="flex gap-1 mt-1.5">
            {([['hoy', 'Hoy'], ['mañana', 'Mañana'], ['semana', 'En 7 días']] as const).map(([cual, etiqueta]) => (
              <button
                key={cual}
                type="button"
                onClick={() => set('dueDate', atajoFecha(cual))}
                className="px-2 py-1 text-xs rounded-md bg-gray-100 text-sereno-dark/60 hover:bg-gray-200 transition-colors"
              >
                {etiqueta}
              </button>
            ))}
            {datos.dueDate && (
              <button
                type="button"
                onClick={() => set('dueDate', null)}
                className="px-2 py-1 text-xs rounded-md text-sereno-dark/40 hover:bg-gray-100 transition-colors"
              >
                Quitar
              </button>
            )}
          </div>
        </div>
        <div>
          <label className="label">Avisar por Telegram</label>
          <input
            type="datetime-local"
            className="input"
            value={aInputLocal(datos.remindAt)}
            onChange={e => set('remindAt', deInputLocal(e.target.value))}
          />
          <p className="text-xs text-sereno-dark/40 mt-1.5">El aviso lo manda el cron; sin fecha, no se avisa.</p>
        </div>
      </div>

      <div>
        <label className="label">Repetir al completarla</label>
        <select
          className="input"
          value={datos.recurrencia}
          onChange={e => set('recurrencia', e.target.value as TaskRecurrencia)}
          disabled={!datos.dueDate}
        >
          {(Object.keys(ETIQUETA_RECURRENCIA) as TaskRecurrencia[]).map(r => (
            <option key={r} value={r}>{ETIQUETA_RECURRENCIA[r]}</option>
          ))}
        </select>
        {!datos.dueDate && (
          <p className="text-xs text-sereno-dark/40 mt-1">Una tarea sin fecha no se puede repetir.</p>
        )}
      </div>

      {!relacionBloqueada && (
        <div>
          <label className="label">Relacionada con</label>
          <SelectorRelacion
            valor={{ relatedType: datos.relatedType, relatedId: datos.relatedId, relatedName: datos.relatedName }}
            onChange={r => setDatos(d => ({ ...d, ...r }))}
          />
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-100">
        {onEliminar ? (
          confirmandoBorrado ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-sereno-dark/60">¿Seguro?</span>
              <button
                type="button"
                onClick={async () => { setGuardando(true); try { await onEliminar(); } finally { setGuardando(false); } }}
                className="text-sm text-red-500 font-medium hover:underline"
              >
                Eliminar
              </button>
              <button type="button" onClick={() => setConfirmandoBorrado(false)} className="text-sm text-sereno-dark/40 hover:underline">
                No
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmandoBorrado(true)}
              className="flex items-center gap-1.5 text-sm text-sereno-dark/40 hover:text-red-500 transition-colors"
            >
              <Trash2 size={14} /> Eliminar
            </button>
          )
        ) : <span />}

        <div className="flex gap-3">
          <button type="button" onClick={onCerrar} className="btn-ghost">Cancelar</button>
          <button type="submit" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </form>
  );
}
